class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
 
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
    
    // Create ICR event handler once to prevent duplicate listeners (Bug Fix)
    this.handleICRClick = (event) => {
      // Ensure this handler only responds to events from within this component
      if (event.target.closest('cart-drawer')) {
        return; // Ignore events bubbling up from the cart drawer
      }

      // Check if target or closest parent has the data attribute
      const learnMoreBtn = event.target.closest('[data-icr-learn-more]');
      const addCartBtn = event.target.closest('[data-icr-add-cart]');
      
      if (learnMoreBtn) {
        event.preventDefault();
        event.stopPropagation();
        this.handleICRLearnMore(event);
      } else if (addCartBtn) {
        event.preventDefault();
        event.stopPropagation();
        this.handleICRAddCart(event);
      }
    };
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    // TEMPORARILY DISABLED: Cross-sync between cart page and drawer
    // This prevents content mixing issues while maintaining individual functionality
    // TODO: Re-enable once section isolation is confirmed working
    // Subscribe to cart updates from cart drawer and other sources
    this.cartUpdateUnsubscriber = subscribe('cart-update', (event) => {
      if (event.source === 'cart-items') {
        return; // Don't update if the change came from this component itself
      }
      
      // SAFETY: Only update main cart if we're actually on the cart page
      if (window.location.pathname === '/cart' && !document.hidden) {
        if (window.cartPerformanceDebug) {
          console.log('🔄 Syncing main cart from cart-drawer changes');
        }
        this.onCartUpdateFromDrawer();
      }
    });
    
    // Initialize ICR upsell functionality
    this.initializeICRUpsell();
    
    // Also check for existing ICR adapters in cart and hide appropriate upsells
    setTimeout(() => {
      this.hideICRUpsellsForCompletedItems();
      this.reorderICRAdapters(); // Reorder items on initial load
    }, 100);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) {
          this.innerHTML = sourceQty.innerHTML;
          // Re-initialize ICR upsell after content update
          this.initializeICRUpsell();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  onCartUpdateSilent() {
    // Update cart content without publishing events (to prevent loops)
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) {
          this.innerHTML = sourceQty.innerHTML;
          // Re-initialize ICR upsell after content update
          this.initializeICRUpsell();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  // Handle cart updates from cart drawer using section manager
  onCartUpdateFromDrawer() {
    
    // Use the performance manager's section update mechanism for consistency
    if (window.cartPerformanceManager && window.cartPerformanceManager.updateCartSectionsAfterICR) {
      // Create a fake response to trigger the section update mechanism
      const fakeResponse = new Response(JSON.stringify({
        success: true,
        message: 'Cart drawer sync update',
        source: 'cart-drawer'
      }), {
        status: 200,
        statusText: 'OK',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const syncDetails = {
        lineItemKey: 'drawer-sync',
        source: 'cart-drawer',
        type: 'sync-update'
      };
      
      // Trigger section updates
      window.cartPerformanceManager.updateCartSectionsAfterICR(fakeResponse, syncDetails)
        .catch(error => {
          console.warn('❌ Failed to sync main cart with drawer:', error);
        });
    } else {
      // Fallback to original method if performance manager not available
      this.onCartUpdateSilent();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const sectionsToRequest = this.getSectionsToRender().map((section) => section.section).join(',');
    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToRequest,
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // Use smart section manager for optimized rendering
        if (window.cartSectionManager) {
          // Convert cart.js sections format to standard format for section manager
          const standardSections = this.getSectionsToRender().filter(section => 
            section.id !== 'cart-live-region-text' // Exclude live region from section updates
          ).map(section => ({
            id: section.id,
            selector: section.selector
          }));
          
          // Create compatible parsedState for section manager
          const compatibleState = {
            sections: {}
          };
          
          standardSections.forEach(section => {
            const originalSection = this.getSectionsToRender().find(s => s.id === section.id);
            compatibleState.sections[section.id] = parsedState.sections[originalSection.section];
          });
          window.cartSectionManager.smartRender(this, compatibleState, standardSections);
        } else {
          // Fallback to original rendering (excluding live region)
          this.getSectionsToRender().forEach((section) => {
            // Skip the live region section to prevent content replacement
            if (section.id === 'cart-live-region-text') return;
            
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            
            if (elementToReplace && parsedState.sections[section.section]) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          });
        }

        // Re-initialize ICR upsell after all updates
        this.initializeICRUpsell();
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }
        // Publish cart update event so cart drawer can sync
        publish('cart-update', { 
          source: 'cart-items',
          item_count: parsedState.item_count,
          total_price: parsedState.total_price 
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    
    if (cartStatus && message) {
      // Set the actual message content
      cartStatus.textContent = message;
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
        cartStatus.textContent = ''; // Clear the message
      }, 3000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  // ICR Upsell functionality for main cart page - supports multiple line item containers
  initializeICRUpsell() {
    // Check if ICR upsell is enabled
    if (!window.themeSettings?.icr_upsell_enabled) return;
    
    // Check if ICR adapter product is available/in stock
    if (!window.themeSettings?.icr_adapter_available) return;
    
    // Find all line-item ICR containers
    const icrContainers = this.querySelectorAll('.line-item-icr-upsell');
    if (!icrContainers.length) return;

    // Remove existing listeners from ALL containers first (Bug Fix)
    const allContainers = this.querySelectorAll('.line-item-icr-upsell');
    allContainers.forEach(container => {
      container.removeEventListener('click', this.handleICRClick);
    });
    
    // Add event listener to new containers using the pre-created handler
    icrContainers.forEach(container => {
      container.addEventListener('click', this.handleICRClick);
    });
  }

  handleICRLearnMore(event) {
    const icrProductHandle = window.themeSettings?.icr_adapter_product_handle;
    
    if (!icrProductHandle) {
      console.error('ICR adapter product not configured');
      return;
    }

    // Navigate directly to the ICR product page
    window.location.href = `/products/${icrProductHandle}`;
  }

  async handleICRAddCart(event) {
    const addCartBtn = event.target.closest('[data-icr-add-cart]');
    const icrContainer = event.target.closest('.line-item-icr-upsell');
    
    if (!addCartBtn || !icrContainer) return;

    // Get line item context
    const lineItemKey = icrContainer.dataset.lineItemKey;
    const productId = icrContainer.dataset.productId;

    // Phase 2: OPTIMISTIC UI - Hide upsell immediately for instant feedback

    this.hideICRUpsellOptimistically(icrContainer, addCartBtn);

    // Show loading state
    this.setICRButtonLoading(addCartBtn, true);

    try {
      // Get ICR adapter product handle
      const icrProductHandle = window.themeSettings?.icr_adapter_product_handle;
      
      if (!icrProductHandle) {
        throw new Error('ICR adapter product not configured');
      }

      // Fetch the ICR product to get the variant ID
      const productResponse = await fetch(`/products/${icrProductHandle}.js`);
      if (!productResponse.ok) {
        throw new Error('Failed to fetch ICR adapter product');
      }
      
      const icrProduct = await productResponse.json();
      const variantId = icrProduct.variants?.[0]?.id;

      if (!variantId) {
        throw new Error('No variant found for ICR adapter product');
      }

      // CONSOLIDATION FIX: Check if ICR adapter already exists for this product
      const existingICRItem = await this.findExistingICRAdapter(productId, variantId);
      
      if (existingICRItem) {
        console.log('🔄 Main cart: ICR adapter already exists, updating quantity instead of adding new item');
        await this.updateICRAdapterQuantity(existingICRItem.key, existingICRItem.quantity + 1);
        
        // Still trigger the success flow
        this.showOptimisticSuccessMessage(addCartBtn, icrContainer);
        return;
      }

      // Add to cart - exempt from performance manager batching via header
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-ICR-Request': 'true', // Flag for exemption
          'X-Line-Item-Context': lineItemKey // Track which line item triggered this
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              '_icr_for_product': productId,
              '_icr_for_line_item': lineItemKey,
              '_icr_source': 'cart'
            }
          }]
        })
      });
      
      if (!cartResponse.ok) {
        const errorData = await cartResponse.json();
        throw new Error(errorData.message || 'Failed to add ICR adapter to cart');
      }

      // Phase 2: API succeeded - keep container hidden and show brief success message
      
      this.showOptimisticSuccessMessage(addCartBtn, icrContainer);
      
              // Phase 3: Ensure any ICR upsells for items that now have adapters stay hidden (prevent flash)
        setTimeout(() => {
          // Record which upsells should stay hidden to prevent flash
          const hiddenUpsells = new Set();
          this.querySelectorAll('.line-item-icr-upsell.icr-optimistic-hiding, .line-item-icr-upsell.icr-adapter-present').forEach(upsell => {
            const lineItemKey = upsell.dataset.lineItemKey;
            if (lineItemKey) {
              hiddenUpsells.add(lineItemKey);
              console.log(`🚫 PHASE 3: Main cart marking line item ${lineItemKey} to stay hidden (prevent flash)`);
            }
          });
          
          // If the page refreshes or updates, immediately hide marked upsells
          if (hiddenUpsells.size > 0) {
            hiddenUpsells.forEach(lineItemKey => {
              const upsellContainer = this.querySelector(`.line-item-icr-upsell[data-line-item-key="${lineItemKey}"]`);
              if (upsellContainer && !upsellContainer.classList.contains('icr-adapter-present')) {
                console.log(`⚡ PHASE 3: Main cart immediately hiding upsell for ${lineItemKey} (prevent flash)`);
                
                // Apply instant hide class for flash prevention
                upsellContainer.classList.add('icr-flash-hide', 'icr-adapter-present');
                
                // Remove flash-hide class after DOM settles and re-enable transitions
                requestAnimationFrame(() => {
                  if (upsellContainer) {
                    upsellContainer.classList.remove('icr-flash-hide');
                  }
                });
              }
            });
          }
          
          // Run comprehensive check for any new adapters
          this.hideICRUpsellsForCompletedItems();
          
          // Reorder ICR adapters to appear below their related products
          this.reorderICRAdapters();
        }, 500);

    } catch (error) {
      // Phase 2: API failed - restore upsell with error message
      
      this.restoreICRUpsellOnError(icrContainer, addCartBtn, error.message);
    }
  }

  showICRSuccessMessage(addCartBtn, icrContainer) {
    if (!addCartBtn) return;
    
    // Remove loading class and spinner
    addCartBtn.classList.remove('loading');
    const spinner = addCartBtn.querySelector('.loading-overlay__spinner');
    if (spinner) {
      spinner.classList.add('hidden');
    }
    
    // Update button text
    const buttonSpan = addCartBtn.querySelector('span');
    if (buttonSpan) {
      buttonSpan.textContent = 'Added';
    } else {
      addCartBtn.textContent = 'Added';
    }

    // Hide the entire ICR container after 3 seconds
    setTimeout(() => {
      if (icrContainer) {
        icrContainer.style.transition = 'opacity 0.3s ease-out, height 0.3s ease-out';
        icrContainer.style.opacity = '0';
        icrContainer.style.height = '0';
        icrContainer.style.overflow = 'hidden';
        
        // Remove from DOM after animation
        setTimeout(() => {
          if (icrContainer.parentNode) {
            icrContainer.parentNode.removeChild(icrContainer);
          }
        }, 300);
      }
    }, 3000);
  }

  handleICRError(errorMessage, addCartBtn) {
    // Show error message (could enhance this with better error display)
    alert(`ICR Upsell Error: ${errorMessage}`);
    
    // Reset button state
    if (addCartBtn) {
      this.setICRButtonLoading(addCartBtn, false);
      const buttonSpan = addCartBtn.querySelector('span');
      if (buttonSpan) {
        buttonSpan.textContent = 'Add to cart';
      } else {
        addCartBtn.textContent = 'Add to cart';
      }
    }
  }

  setICRButtonLoading(button, loading) {
    const spinner = button.querySelector('.loading-overlay__spinner');
    const buttonSpan = button.querySelector('span');
    
    if (loading) { 
      button.setAttribute('aria-disabled', true);
      button.classList.add('loading');
      
      // Show spinner
      if (spinner) {
        spinner.classList.remove('hidden');
      }
      
      // Update text
      if (buttonSpan) { 
        buttonSpan.textContent = 'Adding...';
      } else {
        button.textContent = 'Adding...';
      }
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      
      // Hide spinner
      if (spinner) {
        spinner.classList.add('hidden');
      }
      
      // Don't reset text here - let showICRSuccessMessage handle it
    }
  }

  // Phase 2: Optimistic UI Methods for Main Cart

  hideICRUpsellOptimistically(icrContainer, addCartBtn) {
    // Instead of hiding, show loading state
    icrContainer.classList.add('icr-loading');
    addCartBtn.classList.add('loading');
    addCartBtn.setAttribute('aria-disabled', 'true');
    addCartBtn.disabled = true;
    
    // Store original state for potential restoration
    icrContainer._originalButtonText = addCartBtn.textContent;
    icrContainer._originalButtonClasses = addCartBtn.className;
    icrContainer._wasOptimisticallyHidden = true;

  }

  showOptimisticSuccessMessage(addCartBtn, icrContainer) {
    // Reset button loading state
    this.setICRButtonLoading(addCartBtn, false);
    
    // Now hide the upsell after successful addition
    icrContainer.classList.remove('icr-loading');
    icrContainer.classList.add('icr-optimistic-hiding');
    
    // Apply smooth hiding transition
    icrContainer.style.opacity = '0';
    icrContainer.style.maxHeight = '0px';
    icrContainer.style.overflow = 'hidden';
    icrContainer.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
    
    // Keep container hidden - don't restore
    // The success state is that the upsell is no longer needed

  }

  restoreICRUpsellOnError(icrContainer, addCartBtn, errorMessage) {
    // Only restore if it was optimistically hidden
    if (!icrContainer._wasOptimisticallyHidden) return;
    
    // Remove optimistic hiding class
    icrContainer.classList.remove('icr-optimistic-hiding');
    
    // Add restoring animation class
    icrContainer.classList.add('icr-restoring');
    
    // Restore original state with smooth animation
    setTimeout(() => {
      icrContainer.style.opacity = icrContainer._originalOpacity;
      icrContainer.style.maxHeight = icrContainer._originalMaxHeight || 'auto';
      icrContainer.style.overflow = icrContainer._originalOverflow;
      
      // Remove animation class after animation completes
      setTimeout(() => {
        icrContainer.classList.remove('icr-restoring');
      }, 300);
    }, 50);
    
    // Reset button loading state and show error via existing method
    this.handleICRError(errorMessage, addCartBtn);
    
    console.error('🚀 PHASE 2: Main cart ICR upsell restored due to error:', errorMessage);
    
    // Clean up optimistic state
    delete icrContainer._originalOpacity;
    delete icrContainer._originalMaxHeight;
    delete icrContainer._originalOverflow;
    delete icrContainer._wasOptimisticallyHidden;
  }

  // ICR reordering disabled - section manager handles all ICR positioning
  reorderICRAdapters() {
    if (window.cartPerformanceDebug) {
      console.log('🔄 Main cart: ICR reordering disabled - section manager handles ICR positioning');
    }
    // DISABLED: Section manager now handles all ICR positioning and state management
    // This prevents conflicts between cart.js and section updates
    return;
  }

  // CONSOLIDATION FIX: Find existing ICR adapter in cart for the same product
  async findExistingICRAdapter(productId, variantId) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Look for existing ICR adapter with matching product ID or variant ID
      const existingItem = cart.items.find(item => {
        // Check if it's the same variant (ICR adapter product)
        if (item.variant_id === variantId) {
          // Check if it's for the same product (via properties)
          if (item.properties && item.properties._icr_for_product === productId) {
            return true;
          }
          // Also check if it has no specific product context (generic ICR adapter)
          if (!item.properties || (!item.properties._icr_for_product && !item.properties._icr_for_line_item)) {
            return true;
          }
        }
        return false;
      });

      return existingItem || null;
    } catch (error) {
      console.warn('Main cart: Failed to check for existing ICR adapter:', error);
      return null;
    }
  }

  // CONSOLIDATION FIX: Update quantity of existing ICR adapter
  async updateICRAdapterQuantity(itemKey, newQuantity) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: newQuantity
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update ICR adapter quantity');
      }

      console.log(`🔄 Main cart: Successfully updated ICR adapter quantity to ${newQuantity}`);
      
      // Trigger page refresh or cart update for main cart
      // Since main cart is a full page, we can just reload
      window.location.reload();
      
    } catch (error) {
      console.error('Main cart: Failed to update ICR adapter quantity:', error);
      throw error;
    }
  }

  // Phase 3: Hide ICR upsells for line items that now have ICR adapters in cart
  hideICRUpsellsForCompletedItems() {
    if (window.cartPerformanceDebug) {
      console.log('🔄 PHASE 3: ICR upsell hiding enabled for initial page load detection');
    }
    
    try {
      // Get current cart state to check for ICR adapters
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {

          // Find all ICR adapters in the cart
          const icrAdapterProductId = window.themeSettings?.icr_adapter_product_id;
          const allIcrAdapters = cart.items.filter(item => {
            // Check if it's an ICR adapter by product ID from theme settings or product name
            return (icrAdapterProductId && item.product_id === parseInt(icrAdapterProductId)) ||
                   (item.title && item.title.toLowerCase().includes('icr adapter')) ||
                   (item.title && item.title.toLowerCase().includes('internal cable routing'));
          });

          // Find ICR adapters with targeting properties (for specific hiding)
          const targetedIcrAdapters = allIcrAdapters.filter(item => {
            return item.properties && (
              item.properties._icr_for_line_item ||  // Cart drawer added
              item.properties._icr_for_product ||    // PDP added  
              item.properties._icr_source            // Either source
            );
          });

          // If ANY ICR adapter is in the cart, hide ALL ICR upsells
          if (allIcrAdapters.length > 0) {
            console.log(`🚫 Found ${allIcrAdapters.length} ICR adapter(s) in cart - hiding all ICR upsells`);
            
            // Hide all ICR upsells on the page
            const allUpsells = this.querySelectorAll('.line-item-icr-upsell');
            allUpsells.forEach(upsellContainer => {
              upsellContainer.classList.remove('icr-optimistic-hiding');
              upsellContainer.classList.add('icr-adapter-present');
              
              // Ensure it stays hidden with the adapter-present styling
              upsellContainer.style.opacity = '0';
              upsellContainer.style.maxHeight = '0px';
              upsellContainer.style.overflow = 'hidden';
              upsellContainer.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
              
              // Clean up any optimistic state tracking
              delete upsellContainer._originalOpacity;
              delete upsellContainer._originalMaxHeight;
              delete upsellContainer._originalOverflow;
              delete upsellContainer._wasOptimisticallyHidden;
            });
          }

          // Additionally, hide upsells for specific targeted line items
          targetedIcrAdapters.forEach(adapter => {
            // Handle both cart drawer added (has _icr_for_line_item) and PDP added (has _icr_for_product)
            if (adapter.properties._icr_for_line_item) {
              // Cart drawer added adapter - hide specific line item upsell
              const lineItemKey = adapter.properties._icr_for_line_item;
              
              // Try multiple selectors to find the upsell container
              let upsellContainer = this.querySelector(`[data-line-item-key="${lineItemKey}"] .line-item-icr-upsell`);
              
              if (!upsellContainer) {
                // Alternative selector - look for the exact line item key
                upsellContainer = this.querySelector(`.line-item-icr-upsell[data-line-item-key="${lineItemKey}"]`);
              }
              
              if (upsellContainer) {

              // Remove optimistic hiding class and apply permanent hiding
              upsellContainer.classList.remove('icr-optimistic-hiding');
              upsellContainer.classList.add('icr-adapter-present');
              
              // Ensure it stays hidden with the adapter-present styling
              upsellContainer.style.opacity = '0';
              upsellContainer.style.maxHeight = '0px';
              upsellContainer.style.overflow = 'hidden';
              upsellContainer.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
              
              // Clean up any optimistic state tracking
              delete upsellContainer._originalOpacity;
              delete upsellContainer._originalMaxHeight;
              delete upsellContainer._originalOverflow;
              delete upsellContainer._wasOptimisticallyHidden;
            } else {
              console.warn(`Could not find ICR upsell container for line item ${lineItemKey}`);
            }
            
            } else if (adapter.properties._icr_for_product) {
              // PDP added adapter - hide all upsells for this product
              const productId = adapter.properties._icr_for_product;
              
              // Find all ICR upsells for this product
              const productUpsells = this.querySelectorAll(`.line-item-icr-upsell[data-product-id="${productId}"]`);

              productUpsells.forEach(upsellContainer => {

                // Remove optimistic hiding class and apply permanent hiding
                upsellContainer.classList.remove('icr-optimistic-hiding');
                upsellContainer.classList.add('icr-adapter-present');
                
                // Ensure it stays hidden with the adapter-present styling
                upsellContainer.style.opacity = '0';
                upsellContainer.style.maxHeight = '0px';
                upsellContainer.style.overflow = 'hidden';
                upsellContainer.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
                
                // Clean up any optimistic state tracking
                delete upsellContainer._originalOpacity;
                delete upsellContainer._originalMaxHeight;
                delete upsellContainer._originalOverflow;
                delete upsellContainer._wasOptimisticallyHidden;
              });
            }
          });
        })
        .catch(error => {
          console.warn('🚀 PHASE 3: Main cart failed to check cart for ICR adapters:', error);
        });
    } catch (error) {
      console.warn('🚀 PHASE 3: Main cart error in hideICRUpsellsForCompletedItems:', error);
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
